import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
import QuizInstructions from "./QuizInstructions";
import './QuizSelection.css'
import Navbar from './Navbar/Navbar';

const QuizSelection = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [quizDetails, setQuizDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [couponCode, setCouponCode] = useState('');
    const [discount, setDiscount] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);
    const [userAttempts, setUserAttempts] = useState([]);
    const [isBlocked, setIsBlocked] = useState(false);
    const [canResume, setCanResume] = useState(false);
    const [isCouponValid, setIsCouponValid] = useState(false);


    useEffect(() => {
            const fetchQuizDetails = async () => {
                if (!user) return;

                try {
                    setLoading(true);

                    // Fetch quiz details
                    const { data: quizData, error: quizError } = await supabase
                        .from('quiz_details')
                        .select('quiz_id, quiz_name, price')
                        .eq('is_active', true)
                        .single();

                    if (quizError) throw quizError;
                    setQuizDetails(quizData);

                    // Fetch user's quiz attempts
                    const { data: attemptsData, error: attemptsError } = await supabase
                        .from('quiz_attempts')
                        .select('*')
                        .eq('user_id', user.id)
                        .order('created_at', { ascending: false });

                    if (attemptsError) throw attemptsError;
                    setUserAttempts(attemptsData);

                    // Check if user is blocked
                    const hasCompleted = attemptsData.some(a => a.is_completed);
                    const hasExceededResumes = attemptsData.some(a =>
                        !a.is_completed && a.resume_count >= 4
                    );

                    setIsBlocked(hasCompleted || hasExceededResumes);

                    // Check if user can resume
                    const incompleteAttempt = attemptsData.find(a =>
                        !a.is_completed && a.resume_count < 4
                    );
                    setCanResume(!!incompleteAttempt);

                } catch (error) {
                    setError('Failed to load quiz details');
                    console.error('Error:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchQuizDetails();
        }, [user]);

    const applyCoupon = async () => {
        try {
            setError('');
            setSuccess('');
            setIsCouponValid(false);

            if (!couponCode.trim()) {
                setError('Coupon code is required');
                return;
            }

            if (!quizDetails) {
                setError('Quiz details are not loaded yet');
                return;
            }

            const currentDate = new Date().toISOString();

            // Validate coupon against database
            const { data: coupon, error } = await supabase
                .from('coupons')
                .select('*')
                .eq('code', couponCode.trim())
                .eq('quiz_id', quizDetails.quiz_id)
                .eq('is_active', true)
                .lte('valid_from', currentDate)
                .gte('valid_until', currentDate)
                .single();

            if (error || !coupon) {
                setError('Invalid or expired coupon code');
                return;
            }

            if (coupon.max_uses && coupon.current_uses >= coupon.max_uses) {
                setError('Coupon usage limit reached');
                return;
            }

            // Update coupon usage
            const { error: updateError } = await supabase
                .from('coupons')
                .update({ current_uses: coupon.current_uses + 1 })
                .eq('coupon_id', coupon.coupon_id);

            if (updateError) throw updateError;

            setDiscount(coupon.discount_percentage);
            setSuccess('Coupon applied successfully!');
            setIsCouponValid(true);

        } catch (error) {
            setError('Failed to apply coupon');
            console.error('Coupon error:', error);
        }
    };

    if (loading) {
        return <div className="quiz-selection-container">
            <div className="loading">Loading quiz details...</div>
        </div>;
    }

    if (!user) {
        navigate('/login');
        return null;
    }

    if (!quizDetails) {
        return <div className="quiz-selection-container">
            <div className="error-message">No active quiz found</div>
        </div>;
    }


    // Previously, users were hard-blocked here when attempts were exhausted or a prior attempt was completed.
    // We now allow users to proceed with a valid coupon code even after completion.

    return (
        <div className="quiz-selection-container">
            <Navbar/>
            <div className="quiz-selection-card">
                <div className="quiz-details">
                    <div className="quiz-title">
                        <h2>{quizDetails.quiz_name}</h2>
                    </div>

                    {!canResume && (
                        <>
                        <div className="price-section">
                            <div className="original-price">
                                Original Price: ₹{quizDetails.price.toFixed(2)}
                            </div>
                            {discount > 0 && (
                                <div className="discount">
                                    Discount: {discount}% off
                                </div>
                            )}
                            <div className="final-price">
                                Final Price: ₹{(quizDetails.price * (1 - discount / 100)).toFixed(2)}
                            </div>
                        </div>

                            <div className="coupon-section">
                                <div className="coupon-input">
                                    <input
                                        type="text"
                                        placeholder="Enter coupon code *"
                                        value={couponCode}
                                        onChange={(e) => {
                                            setCouponCode(e.target.value);
                                            // Reset validation when code changes
                                            setIsCouponValid(false);
                                            setSuccess('');
                                        }}
                                        required
                                    />
                                    <button
                                        className="apply-button"
                                        onClick={applyCoupon}
                                        disabled={!couponCode.trim()}
                                    >
                                        Apply
                                    </button>
                                </div>

                        {error && (
                            <div className="error-message">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        {success && (
                            <div className="success-message">
                                <CheckCircle2 size={16} />
                                <span>{success}</span>
                            </div>
                        )}
                    </div>
</>
                        )}
                    {/* Replace the existing button with this conditional rendering */}
                    {(canResume || isCouponValid) && (
                        <button
                            className={`start-quiz-button ${(canResume || isCouponValid) ? 'show' : ''}`}
                            onClick={() => setShowInstructions(true)}
                        >
                            {canResume ? 'Resume Assessment' : 'Proceed'}
                            <ArrowRight size={20} />
                        </button>
                    )}

                    {!canResume && (
                        <div className="coupon-note">
                            * Valid coupon code required to attempt the quiz
                        </div>
                    )}
                </div>
            </div>

            {showInstructions && (
                <div className="instructions-overlay">
                    <div className="instructions-card">
                        <QuizInstructions
                            onStartQuiz={() => navigate('/quiz', { state: { fromQuizSelection: true } })}
                            onClose={() => setShowInstructions(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizSelection;